import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {ModelResponse, ResponseData} from "@dvrd/dvr-controls";
import {AccessKey, UserRight, UserRightType, UserRole} from "../util/interfaces";
import User from "./userModel";
import {
    checkInvitation,
    deleteInvitation,
    getInvitations,
    inviteAccountUser,
    sendInvitation,
    updateInvitation
} from "../util/requestSender";
import IDate from '@dvrd/idate';
import {deepClone} from "../util/utils";

interface Props extends BaseProps {
    account_id?: string;
    invited_by?: string;
    email?: string;
    last_sent_at?: string;
    user_role?: UserRole | null;
    rights?: Array<UserRight>;
    invited_by_user?: User | any;
}

export interface InvitationFields extends BaseFields {
    account_id: string;
    invited_by: string;
    email: string;
    last_sent_at: string;
    user_role: UserRole | null;
    rights: Array<UserRight>;
    invited_by_user: User | null;
}

const fields: InvitationFields = {
    id: '',
    created_at: '',
    updated_at: '',
    account_id: '',
    invited_by: '',
    email: '',
    last_sent_at: '',
    user_role: null,
    rights: [],
    invited_by_user: null,
};

const fieldsDefinition: { [key in keyof Omit<InvitationFields, 'invited_by_user'>]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    account_id: createFieldDef(ModelFieldType.STRING),
    invited_by: createFieldDef(ModelFieldType.STRING),
    email: createFieldDef(ModelFieldType.STRING),
    last_sent_at: createFieldDef(ModelFieldType.STRING),
    user_role: createFieldDef(ModelFieldType.STRING, null, true),
    rights: createFieldDef(ModelFieldType.ARRAY),
};

const ignoreProps: Array<keyof Props> = ['invited_by_user'];

export default class Invitation extends BaseModel<Props, InvitationFields> {
    static check = (id: string, callback: ModelResponse<Invitation | null>) => {
        checkInvitation({
            id, callback: (data: ResponseData) => {
                const invitation: Invitation | null = data.success ? new Invitation(data.invitation) : null;
                callback(invitation, data.success, data);
            }
        });
    }

    static getAll = (accountId: string, search: string | null, page: number, limit: number,
                     callback: ModelResponse<Invitation[]>) => {
        return getInvitations({
            data: {
                account_id: accountId, search, limit, page
            }, callback: (data: ResponseData) => {
                const invitations: Array<Invitation> = data.success ?
                    data.invitations.map((inv: any) => new Invitation(inv)) : [];
                callback(invitations, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get invitedBy(): string {
        return this.fields.invited_by;
    }

    set invitedBy(invited_by: string) {
        this.fields.invited_by = invited_by;
    }

    get email(): string {
        return this.fields.email;
    }

    set email(email: string) {
        this.fields.email = email;
    }

    get lastSentAt(): string {
        return this.fields.last_sent_at;
    }

    set lastSentAt(last_sent_at: string) {
        this.fields.last_sent_at = last_sent_at;
    }

    get lastSentAtMoment(): IDate | null {
        if (!this.fields.last_sent_at) return null;
        return new IDate(this.fields.last_sent_at);
    }

    get userRole(): UserRole | null {
        return this.fields.user_role;
    }

    set userRole(user_role: UserRole | null) {
        this.fields.user_role = user_role;
    }

    get rights(): Array<UserRight> {
        return this.fields.rights;
    }

    set rights(rights: Array<UserRight>) {
        this.fields.rights = rights;
    }

    get invitedByUser(): User | null {
        return this.fields.invited_by_user;
    }

    set invitedByUser(invited_by_user: User | null) {
        this.fields.invited_by_user = invited_by_user;
    }

    clone = (deep: boolean = true): Invitation => {
        let fields = this.fields;
        if(deep) fields = deepClone(fields);
        return new Invitation(fields);
    }

    findRight = (key: string): UserRight | null => {
        return this.rights.find((right: UserRight) => right.key === key) ?? null;
    }

    setRight = (right: UserRight): Invitation => {
        const rights = this.rights.slice();
        for (let i = 0; i < rights.length; i++) {
            if (rights[i].key === right.key) {
                rights.splice(i, 1, right);
                this.rights = rights;
                return this;
            }
        }
        // Not found, add new right
        this.rights = this.rights.concat(right);
        return this;
    }

    setRightValue = (key: AccessKey, value: boolean) => {
        const right: UserRight = this.findRight(key) ?? {key, allow: value, right_type: UserRightType.ACTION};
        return this.setRight(right);
    };

    invite = (callback: ModelResponse<Invitation>) => {
        inviteAccountUser({
            data: {
                rights: this.rights,
                email: this.email,
                user_role: this.userRole!,
                account_id: this.accountId,
            }, callback: (data: ResponseData) => {
                if (data.success && data.invitation) this.build(data.invitation);
                callback(this, data.success, data);
            }
        })
    }

    update = (callback: ModelResponse<Invitation>) => {
        updateInvitation({
            data: {
                invitation_id: this.id,
                email: this.email,
                account_id: this.accountId,
                user_role: this.userRole!,
                rights: this.rights,
            }, callback: (data: ResponseData) => {
                if (data.success) this.build(data.invitation);
                callback(this, data.success, data);
            }
        })
    }

    send = (callback: ModelResponse<Invitation>) => {
        sendInvitation({
            data: {invitation_id: this.id, account_id: this.accountId}, callback: (data: ResponseData) => {
                if (data.success) this.build(data.invitation);
                callback(this, data.success, data);
            }
        });
    }

    delete = (callback: ModelResponse<Invitation>) => {
        deleteInvitation({
            account_id: this.accountId, invitation_id: this.id, callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        });
    }

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.invited_by_user) {
                this.fields.invited_by_user = props.invited_by_user instanceof User ? props.invited_by_user
                    : new User(props.invited_by_user);
            }
        }
    };
}